/**
 * Site C — Glacier Tarn Development (§2.3). Config only; scenarios can wait.
 *
 * Alpine storage feeding two Pelton units through a 6 km tunnel at ~600 m net head. The spillway
 * is an **uncontrolled free crest** — there is nothing to operate. The only levers are generation
 * and one low-level outlet, which is precisely why it is interesting: every reservoir decision has
 * to be made through the powerhouse.
 *
 * `spillway.kind: 'free-crest'` is the capability flag; the gate array is empty and the engine's
 * gate code simply iterates nothing. No engine branch on the site id (CLAUDE.md working rules).
 */

import type { SiteConfig } from '../engine/types';

const G = 9.81;
const C_FREE = 2.15;

export const glacierTarn: SiteConfig = {
  id: 'glacier-tarn',
  name: 'Glacier Tarn Development',
  river: 'Sable River (Tarn Creek headwaters)',
  blurb:
    'An alpine tarn, six kilometres of tunnel, six hundred metres of head and two Pelton wheels. ' +
    'No gates to open — only the machines and one outlet.',

  dam: { crest: 1415.0, parapet: 1416.0, foundation: 1360.0, fsl: 1412.0, mfl: 1414.0, mol: 1378.0 },

  /** Live MOL → FSL is 0.20 − 0.02 = 0.18 × 10⁹ m³ (§2.3), over dead storage below 1378 m. */
  stageStorage: {
    h: [1366, 1372, 1378, 1385, 1392, 1400, 1406, 1412, 1414, 1415],
    v: [0, 0.008e9, 0.02e9, 0.041e9, 0.068e9, 0.108e9, 0.148e9, 0.2e9, 0.218e9, 0.228e9],
  },

  spillway: {
    kind: 'free-crest',
    sill: 1412.0,
    gateCount: 0,
    gateWidth: 40.0,
    gateHeight: 0,
    dischargeCoeff: C_FREE,
    orificeCoeff: C_FREE / Math.sqrt(G),
    overtopCoeff: 1.7,
    hoistRate: 0,
    minNonVibrating: 0,
    adjacencyLimit: 99,
    sequence: [],
    scourFlow: 1e9,
    scourGateHours: 1e9,
    heaterPreheatMinutes: 0,
    iceThresholdC: -99,
    damagedGateFlowCap: 1e9,
  },

  llo: {
    count: 1,
    width: 1.8,
    height: 1.8,
    invert: 1376.0,
    centreline: 1376.9,
    cd: 0.78,
    travelRate: 0.4,
    tdgPercent: 118,
    cavitationMinFrac: 0.1,
    cavitationMaxFrac: 0.55,
    cavitationHwThreshold: 1395,
    cavitationMinutes: 90,
  },

  powerhouse: {
    count: 2,
    ratedMW: 90,
    ratedHeadM: 600,
    ratedFlow: 16.5,
    maxFlow: 18,
    minLoadFrac: 0.2,
    roughZoneLo: 0.08,
    roughZoneHi: 0.2,
    roughZoneMinutes: 60,
    snlFlow: 1.2,
    intakeGateSeconds: 240,
    syncSeconds: 120,
    stopSeconds: 180,
    startCost: 800,
    tripHoldMinutes: 10,
    genEfficiency: 0.985,
    stationServiceMW: 1,
    // Six kilometres of tunnel: a much larger friction coefficient than a short penstock.
    penstockK: 0.055,
    mva: 95,
    rpm: 375,
    governorMWPerS: 12,
    // Pelton efficiency is broad and flat-ish, peaking near 0.90.
    efficiency: {
      kind: 'flat-top',
      etaMax: 0.9,
      flatLo: 0.45,
      flatHi: 1.0,
      etaLowQ: 0.2,
      etaLowValue: 0.72,
      kh: 0.5,
      etaMin: 0.5,
    },
    wicket: { linear: 0.2, exp: 1.2 },
    rackDpDerateThreshold: 0.6,
    rakeMinutes: 30,
    tdgPercent: 101,
  },

  tailwater: { kind: 'power', base: 806.0, coeff: 0.05, exp: 0.85 },

  downstream: {
    gaugeName: 'Tarn Creek confluence',
    distanceKm: 9,
    muskingumK: 1.2,
    muskingumX: 0.15,
    localInflowBase: 6,
    localInflowName: 'Marmot Creek',
    floodMinor: 120,
    floodModerate: 180,
    floodMajor: 260,
  },

  licence: {
    minFlow: 2.5,
    minFlowWinter: 1.5,
    winterMonths: [11, 12, 1, 2, 3],
    downRampMPerH: 0.15,
    upRampMPerH: 0.35,
    tdgLimit: 110,
    tdgFloodLimit: 115,
    waiverDelayMinMinutes: 45,
    waiverDelayMaxMinutes: 120,
  },

  ruleCurve: {
    monthly: [1396, 1391, 1387, 1384, 1388, 1398, 1406, 1411, 1412, 1409, 1404, 1400],
    band: 4,
  },

  grid: {
    name: 'Sable Interconnection',
    exportLimitMW: 200,
    priceNight: 22,
    priceShoulder: 45,
    pricePeakMorning: 70,
    pricePeakEvening: 95,
    weekendFactor: 0.85,
    reserveObligationMW: 25,
    reservePayment: 6,
    reservePenalty: 60,
    agcPayment: 12,
    agcRangeMW: 15,
  },

  hydrology: {
    basinAreaKm2: 310,
    meanInflow: 14,
    // Glacier-fed: almost all the area is high.
    bandWeights: [0.1, 0.3, 0.6],
    bandElevations: [1500, 2100, 2700],
    refElevation: 1412,
    sweArealMm: 150,
    ddf: 5.5,
    lapseRate: -0.0065,
    rainOnSnowFactor: 1.4,
    snowThresholdC: 0.5,
    uhRainPeakH: 6,
    uhMeltPeakH: 10,
    uhBaseH: 24,
    baseflowK: 0.97,
    baseflowRecharge: 0.25,
    runoffCMin: 0.4,
    runoffCMax: 0.85,
    wetnessTauDays: 6,
    evapMaxMmDay: 2.0,
    evapPeakDoy: 200,
    seepageBase: 0.2,
    upstreamLagH: 2,
    runoffScale: 1.0,
  },

  tdg: { base: 104, slope: 0.5, cap: 135, degasTauH: 8 },

  lock: null,
};
